import { gql, ApolloClient, InMemoryCache } from "@apollo/client";
import { absolutePublicAssetUrl } from "../data/seo/siteEntity";

// GraphQL queries
const GET_SYSTEM_SETTINGS = gql`
    query GetSystemSettings {
        publicSystemSettings {
            key
            value
            type
        }
    }
`;

// Apollo client instance for settings (separate from main app client)
const settingsClient = new ApolloClient({
    uri: (import.meta as any).env?.VITE_GRAPHQL_URL || "/graphql",
    cache: new InMemoryCache(),
    defaultOptions: {
        watchQuery: {
            errorPolicy: "all",
        },
        query: {
            errorPolicy: "all",
        },
    },
});

export interface SystemSettings {
    appName?: string;
    appUrl?: string;
    supportEmail?: string;
    timezone?: string;
    maintenanceMode?: boolean;
    maintenanceMessage?: string;
    maxUploadSize?: number;
    sessionTimeout?: number;
    metaTitle?: string;
    metaDescription?: string;
    metaKeywords?: string;
    robotsTxtContent?: string;
    sitemapXmlContent?: string;
    enableCustomRobots?: boolean;
    enableCustomSitemap?: boolean;
    ogImage?: string;
    googleTagManagerId?: string;
    enableGoogleTagManager?: boolean;
    gtmHeadScript?: string;
    gtmBodyScript?: string;
    [key: string]: string | number | boolean | undefined;
}

class SettingsService {
    private static settingsCache: SystemSettings | null = null;
    private static cacheExpiry: number = 0;
    private static readonly CACHE_DURATION = 5 * 60 * 1000; // 5 minutes
    private static readonly BOOLEAN_SETTING_KEYS = new Set<string>(["maintenanceMode", "enableCustomRobots", "enableCustomSitemap", "enableGoogleTagManager", "sessionSecure"]);
    private static readonly NUMBER_SETTING_KEYS = new Set<string>(["maxUploadSize", "sessionTimeout", "maxQueriesPerConversation"]);

    private static parseBooleanSetting(value: unknown): boolean {
        if (typeof value === "boolean") return value;
        if (typeof value === "number") return value === 1;
        if (typeof value === "string") {
            const normalized = value.trim().toLowerCase();
            return normalized === "true" || normalized === "1" || normalized === "yes" || normalized === "on";
        }
        return false;
    }

    private static parseNumberSetting(value: unknown): number {
        if (typeof value === "number") return value;
        const parsed = Number(value);
        return Number.isNaN(parsed) ? 0 : parsed;
    }

    /**
     * Fetch system settings from the API
     */
    static async getSystemSettings(): Promise<SystemSettings> {
        // Check cache first
        if (this.settingsCache && Date.now() < this.cacheExpiry) {
            return this.settingsCache;
        }

        try {
            const { data } = await settingsClient.query({
                query: GET_SYSTEM_SETTINGS,
                errorPolicy: "all",
            });

            const settings: SystemSettings = {};

            if (data?.publicSystemSettings) {
                data.publicSystemSettings.forEach((setting: any) => {
                    if (setting.key && setting.value !== undefined) {
                        const key = String(setting.key);
                        const value = setting.value;

                        // Convert based on known keys first, then fallback to type.
                        if (this.BOOLEAN_SETTING_KEYS.has(key) || setting.type === "boolean") {
                            settings[key] = this.parseBooleanSetting(value);
                        } else if (this.NUMBER_SETTING_KEYS.has(key) || setting.type === "number") {
                            settings[key] = this.parseNumberSetting(value);
                        } else {
                            settings[key] = value;
                        }
                    }
                });
            }

            // Cache the results
            this.settingsCache = settings;
            this.cacheExpiry = Date.now() + this.CACHE_DURATION;

            return settings;
        } catch (error) {
            console.error("Failed to fetch system settings:", error);
            // Return empty object on error to prevent app crashes
            return {};
        }
    }

    /**
     * Get a specific setting value
     */
    static async getSetting(key: keyof SystemSettings): Promise<string | number | boolean | undefined> {
        const settings = await this.getSystemSettings();
        return settings[key];
    }

    /**
     * Clear the settings cache
     */
    static clearCache(): void {
        this.settingsCache = null;
        this.cacheExpiry = 0;
    }

    /**
     * Inject meta tags into the document head.
     *
     * NOTE: The canonical/og:url here always reflect the *current* path
     * (`window.location.pathname`), not just the bare app URL — otherwise
     * every route would advertise the same canonical and Google would
     * collapse the entire site into the homepage.
     *
     * In normal app usage the `MetaTagsManager` React component owns canonical
     * + GTM injection. This static method exists for one-off bootstrap calls
     * (e.g. error pages) and must stay path-aware too.
     */
    static async injectMetaTags(): Promise<void> {
        try {
            const settings = await this.getSystemSettings();

            // Update document title
            if (settings.metaTitle) {
                document.title = settings.metaTitle;
            }

            // Update or create meta description
            this.updateMetaTag("description", settings.metaDescription || "");

            // Update or create meta keywords
            this.updateMetaTag("keywords", settings.metaKeywords || "");

            const origin = (import.meta.env.VITE_SITE_REGION === "in"
                ? "https://flogpt.in"
                : String(settings.appUrl || window.location.origin)
            ).replace(/\/+$/, "");
            const path = window.location.pathname || "/";
            const canonicalPath = path === "/" ? "/" : path.replace(/\/+$/, "");
            const fullUrl = `${origin}${canonicalPath}`;

            // Update Open Graph tags
            this.updateMetaProperty("og:title", settings.metaTitle || document.title);
            this.updateMetaProperty("og:description", settings.metaDescription || "");
            this.updateMetaProperty("og:image", absolutePublicAssetUrl(settings.ogImage || ""));
            this.updateMetaProperty("og:url", fullUrl);

            // Update Twitter Card tags
            this.updateMetaName("twitter:card", "summary_large_image");
            this.updateMetaName("twitter:title", settings.metaTitle || document.title);
            this.updateMetaName("twitter:description", settings.metaDescription || "");
            this.updateMetaName("twitter:image", absolutePublicAssetUrl(settings.ogImage || ""));

            // Update canonical URL — must include current path so each route
            // resolves to its own canonical (not the homepage).
            let canonicalLink = document.querySelector('link[rel="canonical"]') as HTMLLinkElement;
            if (!canonicalLink) {
                canonicalLink = document.createElement("link");
                canonicalLink.rel = "canonical";
                document.head.appendChild(canonicalLink);
            }
            canonicalLink.href = fullUrl;
        } catch (error) {
            console.error("Failed to inject meta tags:", error);
        }
    }

    /**
     * Inject Google Tag Manager scripts
     */
    static async injectGTMScripts(): Promise<void> {
        try {
            const settings = await this.getSystemSettings();

            if (!settings.enableGoogleTagManager || !settings.googleTagManagerId) {
                return;
            }

            const gtmId = settings.googleTagManagerId;

            // Inject head script
            if (!document.querySelector(`script[data-gtm="${gtmId}"]`)) {
                const headScript = settings.gtmHeadScript || this.generateGTMHeadScript(gtmId);
                const scriptElement = document.createElement("script");
                scriptElement.innerHTML = headScript;
                scriptElement.setAttribute("data-gtm", gtmId);
                document.head.insertBefore(scriptElement, document.head.firstChild);
            }

            // Inject body script (noscript)
            if (!document.querySelector(`noscript[data-gtm="${gtmId}"]`)) {
                const bodyScript = settings.gtmBodyScript || this.generateGTMBodyScript(gtmId);
                const noscriptElement = document.createElement("noscript");
                noscriptElement.innerHTML = bodyScript;
                noscriptElement.setAttribute("data-gtm", gtmId);
                document.body.insertBefore(noscriptElement, document.body.firstChild);
            }
        } catch (error) {
            console.error("Failed to inject GTM scripts:", error);
        }
    }

    /**
     * Check if maintenance mode is enabled
     */
    static async isMaintenanceMode(): Promise<boolean> {
        const settings = await this.getSystemSettings();
        return this.parseBooleanSetting(settings.maintenanceMode);
    }

    /**
     * Get maintenance message
     */
    static async getMaintenanceMessage(): Promise<string> {
        const settings = await this.getSystemSettings();
        return settings.maintenanceMessage || "System is currently under maintenance. Please check back later.";
    }

    /**
     * Helper method to update/create meta tag
     */
    private static updateMetaTag(name: string, content: string): void {
        let metaTag = document.querySelector(`meta[name="${name}"]`) as HTMLMetaElement;
        if (!metaTag) {
            metaTag = document.createElement("meta");
            metaTag.name = name;
            document.head.appendChild(metaTag);
        }
        metaTag.content = content;
    }

    /**
     * Helper method to update/create meta property tag
     */
    private static updateMetaProperty(property: string, content: string): void {
        let metaTag = document.querySelector(`meta[property="${property}"]`) as HTMLMetaElement;
        if (!metaTag) {
            metaTag = document.createElement("meta");
            metaTag.setAttribute("property", property);
            document.head.appendChild(metaTag);
        }
        metaTag.content = content;
    }

    /**
     * Helper method to update/create meta name tag
     */
    private static updateMetaName(name: string, content: string): void {
        let metaTag = document.querySelector(`meta[name="${name}"]`) as HTMLMetaElement;
        if (!metaTag) {
            metaTag = document.createElement("meta");
            metaTag.name = name;
            document.head.appendChild(metaTag);
        }
        metaTag.content = content;
    }

    /**
     * Generate default GTM head script
     */
    private static generateGTMHeadScript(gtmId: string): string {
        return `(function(w,d,s,l,i){w[l]=w[l]||[];w[l].push({'gtm.start':
new Date().getTime(),event:'gtm.js'});var f=d.getElementsByTagName(s)[0],
j=d.createElement(s),dl=l!='dataLayer'?'&l='+l:'';j.async=true;j.src=
'https://www.googletagmanager.com/gtm.js?id='+i+dl;f.parentNode.insertBefore(j,f);
})(window,document,'script','dataLayer','${gtmId}');`;
    }

    /**
     * Generate default GTM body script
     */
    private static generateGTMBodyScript(gtmId: string): string {
        return `<iframe src="https://www.googletagmanager.com/ns.html?id=${gtmId}"
height="0" width="0" style="display:none;visibility:hidden"></iframe>`;
    }
}

export default SettingsService;
